import { GlAlert, GlCollapsibleListbox, GlFormGroup, GlSprintf } from '../../../../../../../../../../node_modules/@gitlab/ui/src/index.js?vue3';
import { uniqueId } from 'lodash-es';
import { getWeekdayNames } from '~/lib/utils/datetime_utility';
import { s__ } from '~/locale';
import glFeatureFlagsMixin from '~/vue_shared/mixins/gl_feature_flags_mixin';
import HelpPageLink from '../../../../../../../../../../app/assets/javascripts/vue_shared/components/help_page_link/help_page_link.vue?vue3';
import TimezoneDropdown from '../../../../../../../../../../app/assets/javascripts/vue_shared/components/timezone_dropdown/timezone_dropdown.vue?vue3';
import { FLOW_TRIGGER_TYPE_SCHEDULE, SCHEDULE_DAY_OF_MONTH_MAX, SCHEDULE_DEFAULT_TIMEZONE, SCHEDULE_FIELD_DAY_OF_MONTH, SCHEDULE_FIELD_DAY_OF_WEEK, SCHEDULE_FIELD_HOUR, SCHEDULE_FIELD_MINUTE, SCHEDULE_FIELD_TIMEZONE, SCHEDULE_FREQUENCIES, SCHEDULE_FREQUENCY_FIELDS, SCHEDULE_HOUR_MAX, SCHEDULE_HOUR_MIN, SCHEDULE_MINUTE_VALUES } from 'ee/ai/duo_agents_platform/constants';
import { parseScheduleConfig, randomizeScheduleDefaults } from 'ee/ai/duo_agents_platform/utils';
const SCHEDULE_SCOPE = FLOW_TRIGGER_TYPE_SCHEDULE.value;
export default {
    name: 'ScheduleEventsConfiguration',
    components: {
        GlAlert,
        GlCollapsibleListbox,
        GlFormGroup,
        GlSprintf,
        HelpPageLink,
        TimezoneDropdown
    },
    mixins: [
        glFeatureFlagsMixin()
    ],
    inject: {
        timezoneData: {
            default: ()=>[]
        },
        userTimezone: {
            default: ''
        }
    },
    props: {
        value: {
            type: Object,
            required: false,
            default: ()=>({})
        },
        invalidFeedback: {
            type: String,
            required: false,
            default: null
        },
        serviceAccountRole: {
            type: String,
            required: false,
            default: null
        }
    },
    emits: [
        'input'
    ],
    data () {
        return {
            hourLabelId: uniqueId('schedule-hour-label-'),
            minuteLabelId: uniqueId('schedule-minute-label-')
        };
    },
    computed: {
        runAtDescription () {
            return this.glFeatures.duoAutomations ? s__('DuoAgentsPlatform|Automations might run a few minutes after the scheduled time to distribute load across the system.') : s__('DuoAgentsPlatform|Triggers might run a few minutes after the scheduled time to distribute load across the system.');
        },
        scheduleWarning () {
            return this.glFeatures.duoAutomations ? s__("DuoAgentsPlatform|Scheduled automations do not use composite identity. They run with only the service account's permissions.") : s__("DuoAgentsPlatform|Scheduled triggers do not use composite identity. They run with only the service account's permissions.");
        },
        scheduleWarningWithRole () {
            return this.glFeatures.duoAutomations ? s__("DuoAgentsPlatform|Scheduled automations do not use composite identity. They run with the service account's %{linkStart}%{role} role%{linkEnd} on this project.") : s__("DuoAgentsPlatform|Scheduled triggers do not use composite identity. They run with the service account's %{linkStart}%{role} role%{linkEnd} on this project.");
        },
        schedule () {
            return parseScheduleConfig(this.value) ?? {};
        },
        frequency () {
            return this.schedule.frequency ?? null;
        },
        activeFields () {
            return SCHEDULE_FREQUENCY_FIELDS[this.frequency] ?? [];
        },
        showMinute () {
            return this.activeFields.includes(SCHEDULE_FIELD_MINUTE);
        },
        showHour () {
            return this.activeFields.includes(SCHEDULE_FIELD_HOUR);
        },
        showDayOfWeek () {
            return this.activeFields.includes(SCHEDULE_FIELD_DAY_OF_WEEK);
        },
        showDayOfMonth () {
            return this.activeFields.includes(SCHEDULE_FIELD_DAY_OF_MONTH);
        },
        showTimezone () {
            return this.activeFields.includes(SCHEDULE_FIELD_TIMEZONE);
        },
        showRunAt () {
            return this.showHour || this.showMinute;
        },
        frequencyToggleText () {
            return SCHEDULE_FREQUENCIES.find((option)=>option.value === this.frequency)?.text ?? s__('DuoAgentsPlatform|Select frequency');
        },
        dayOfWeekItems () {
            return getWeekdayNames().map((text, value)=>({
                    value,
                    text
                }));
        },
        dayOfWeekToggleText () {
            return this.dayOfWeekItems[this.schedule.dayOfWeek]?.text ?? '';
        },
        dayOfMonthItems () {
            return Array.from({
                length: SCHEDULE_DAY_OF_MONTH_MAX
            }, (_, index)=>({
                    value: index + 1,
                    text: String(index + 1)
                }));
        },
        dayOfMonthToggleText () {
            return this.schedule.dayOfMonth != null ? String(this.schedule.dayOfMonth) : '';
        },
        hourItems () {
            return Array.from({
                length: SCHEDULE_HOUR_MAX - SCHEDULE_HOUR_MIN + 1
            }, (_, index)=>{
                const value = SCHEDULE_HOUR_MIN + index;
                return {
                    value,
                    text: String(value).padStart(2, '0')
                };
            });
        },
        hourToggleText () {
            return String(this.schedule.hour ?? 0).padStart(2, '0');
        },
        minuteItems () {
            return SCHEDULE_MINUTE_VALUES.map((value)=>({
                    value,
                    text: String(value).padStart(2, '0')
                }));
        },
        minuteToggleText () {
            return String(this.schedule.minute ?? 0).padStart(2, '0');
        },
        defaultTimezone () {
            // Only default to a zone the dropdown can actually display — storing an identifier absent
            // from `timezoneData` (e.g. a deprecated profile zone) would pass validation yet show blank.
            const isSelectable = (identifier)=>this.timezoneData.some((tz)=>tz.identifier === identifier);
            if (this.userTimezone && isSelectable(this.userTimezone)) return this.userTimezone;
            const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            if (isSelectable(browserTimezone)) return browserTimezone;
            return isSelectable(SCHEDULE_DEFAULT_TIMEZONE) ? SCHEDULE_DEFAULT_TIMEZONE : '';
        }
    },
    methods: {
        emitSchedule (nextSchedule) {
            this.$emit('input', {
                ...this.value,
                [SCHEDULE_SCOPE]: nextSchedule
            });
        },
        patchSchedule (patch) {
            this.emitSchedule({
                ...this.schedule,
                ...patch
            });
        },
        onSelectFrequency (frequency) {
            this.emitSchedule(randomizeScheduleDefaults(frequency, {
                timezone: this.defaultTimezone
            }));
        },
        onSelectHour (hour) {
            this.patchSchedule({
                hour
            });
        },
        onSelectMinute (minute) {
            this.patchSchedule({
                minute
            });
        },
        onSelectDayOfWeek (dayOfWeek) {
            this.patchSchedule({
                dayOfWeek
            });
        },
        onSelectDayOfMonth (dayOfMonth) {
            this.patchSchedule({
                dayOfMonth
            });
        },
        onSelectTimezone (timezone) {
            this.patchSchedule({
                timezone: timezone.identifier
            });
        }
    },
    scheduleWarningRoleLinkText: s__('DuoAgentsPlatform|%{role} role'),
    SCHEDULE_FREQUENCIES
};
